"use client";

import {  } from "@remixicon/react";
import type {
	ArtistSearchResult,
	FestivalSearchResult,
	ShowSearchResult,
	UserSearchResult,
} from "@workspace/types";
import { cn } from "@workspace/ui/lib/utils";
import { Kbd } from "@workspace/ui/components/kbd";
import { Spinner } from "@workspace/ui/components/spinner";
import { useEffect, useRef, useState } from "react";

import { ArtistResult } from "./artist-result";
import { FestivalResult } from "./festival-result";
import { ShowResult } from "./show-result";
import { UserResult } from "./user-result";

export interface SearchResultsProps {
	searchValue: string;
	userResults: UserSearchResult[];
	festivalResults: FestivalSearchResult[];
	showResults: ShowSearchResult[];
	artistResults: ArtistSearchResult[];
	isLoading: boolean;
	error: string | null;
	selectedIndex: number;
	onSelectUser: (username: string) => void;
	onSelectFestival: (festivalSlug: string) => void;
	onSelectShow: (show: ShowSearchResult) => void;
	onSelectArtist: (artist: ArtistSearchResult) => void;
	isMobile?: boolean;
}

export function SearchResults({
	searchValue,
	userResults,
	festivalResults,
	showResults,
	artistResults,
	isLoading,
	error,
	selectedIndex,
	onSelectUser,
	onSelectFestival,
	onSelectShow,
	onSelectArtist,
	isMobile = false,
}: SearchResultsProps) {
	// Detect OS for keyboard shortcut display
	const [isMac, setIsMac] = useState(false);

	useEffect(() => {
		setIsMac(navigator.userAgent.toUpperCase().indexOf("MAC") >= 0);
	}, []);

	const hasResults =
		userResults.length > 0 ||
		festivalResults.length > 0 ||
		showResults.length > 0 ||
		artistResults.length > 0;

	// Track data counts in refs to avoid stale closure
	const dataCountsRef = useRef({
		totalResults: 0,
		hasResults: false,
		searchLength: 0,
		selectedIndex: -1,
	});
	dataCountsRef.current = {
		totalResults:
			festivalResults.length +
			showResults.length +
			artistResults.length +
			userResults.length,
		hasResults,
		searchLength: searchValue.length,
		selectedIndex,
	};

	return (
		<div
			className="flex-1 overflow-y-auto p-2"
			style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.5rem)" }}
		>
			{isLoading ? (
				<div className="flex flex-col items-center justify-center py-6 text-center">
					<Spinner className="h-5 w-5 animate-spin text-muted-foreground" />
					<p className="mt-2 text-muted-foreground text-sm">Searching...</p>
				</div>
			) : error ? (
				<div className="py-6 text-center">
					<p className="mb-1 text-destructive text-sm">Error searching</p>
					<p className="mx-auto max-w-md text-muted-foreground text-xs">
						{error}
					</p>
				</div>
			) : searchValue.trim() === "" ? (
				<div className="py-6 text-center">
					<p className="text-muted-foreground text-sm">Type to search...</p>
					{!isMobile && (
						<p className="mt-2 text-muted-foreground text-xs">
							Press <Kbd>/</Kbd> or <Kbd>{isMac ? "⌘K" : "Ctrl+K"}</Kbd> to open
							search
						</p>
					)}
				</div>
			) : !hasResults ? (
				<div className="py-6 text-center">
					<p className="text-muted-foreground text-sm">No results found</p>
				</div>
			) : (
				<>
					{/* Festival results */}
					{festivalResults.length > 0 && (
						<>
							<div className="p-2 font-medium text-muted-foreground text-xs">
								Festivals
							</div>
							{festivalResults.map((festival, index) => (
								<div
									key={festival.id}
									className={cn(
										index < festivalResults.length - 1 ? "mb-1" : "",
									)}
								>
									<FestivalResult
										festival={festival}
										isSelected={index === selectedIndex}
										onSelect={() => onSelectFestival(festival.slug)}
									/>
								</div>
							))}
						</>
					)}

					{/* Artist results */}
					{artistResults.length > 0 && (
						<>
							<div
								className={cn(
									"p-2 font-medium text-muted-foreground text-xs",
									festivalResults.length > 0
										? "pt-2"
										: "",
								)}
							>
								Artists
							</div>
							{artistResults.map((artist, index) => {
								const realIndex =
									festivalResults.length + index;
								return (
									<div
										key={artist.id}
										className={cn(
											index < artistResults.length - 1 ? "mb-1" : "",
										)}
									>
										<ArtistResult
											artist={artist}
											isSelected={realIndex === selectedIndex}
											onSelect={() => onSelectArtist(artist)}
										/>
									</div>
								);
							})}
						</>
					)}

					{/* Show results */}
					{showResults.length > 0 && (
						<>
							<div
								className={cn(
									"p-2 font-medium text-muted-foreground text-xs",
									festivalResults.length > 0 || artistResults.length > 0
										? "pt-2"
										: "",
								)}
							>
								Shows
							</div>
							{showResults.map((show, index) => {
								const realIndex = festivalResults.length + artistResults.length + index;
								return (
									<div
										key={show.id}
										className={cn(index < showResults.length - 1 ? "mb-1" : "")}
									>
										<ShowResult
											show={show}
											isSelected={realIndex === selectedIndex}
											onSelect={() => onSelectShow(show)}
										/>
									</div>
								);
							})}
						</>
					)}

					{/* User results */}
					{userResults.length > 0 && (
						<>
							<div className="p-2 pt-2 font-medium text-muted-foreground text-xs">
								Members
							</div>
							{userResults.map((user, index) => {
								const realIndex =
									festivalResults.length +
									artistResults.length +
									showResults.length +
									index;
								return (
									<div
										key={user.id}
										className={cn(index < userResults.length - 1 ? "mb-1" : "")}
									>
										<UserResult
											user={user}
											isSelected={realIndex === selectedIndex}
											onSelect={() =>
												user.username && onSelectUser(user.username)
											}
										/>
									</div>
								);
							})}
						</>
					)}
				</>
			)}
		</div>
	);
}
